import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Loader2, ChevronRight, GraduationCap, Calendar, Award, Users2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import FamilyPaymentCart from "@/components/finance/FamilyPaymentCart";

/**
 * Landing page for parents: a beautiful list of their linked children with
 * quick KPIs and a call-to-action to open each child's dashboard.
 */
const ParentHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [kpis, setKpis] = useState({}); // {student_id: {attendance_pct, avg_pct, class_name, current_term}}
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [chRes, seRes] = await Promise.all([
          api.get("/parent/children"),
          api.get("/academic-sessions"),
        ]);
        setSessions(seRes.data);
        const list = chRes.data.children || [];
        setChildren(list);
        // fetch KPI data per child in parallel
        const current = seRes.data.find((s) => s.is_current) || seRes.data[0];
        const term = "first"; // parent home shows the current-ish term summary
        const kpiOut = {};
        await Promise.all(list.map(async (c) => {
          const k = {};
          try {
            const att = (await api.get(`/attendance/student/${c.id}`)).data;
            const total = att.counts.present + att.counts.absent + att.counts.late + att.counts.excused;
            k.attendance_pct = total ? Math.round(100 * (att.counts.present + att.counts.late) / total) : null;
          } catch (e) { k.attendance_pct = null; }
          if (current) {
            try {
              const rc = (await api.get(`/assessments/report-card/${c.id}`, { params: { session_id: current.id, term } })).data;
              k.avg_pct = rc.overall.average_pct;
              k.grade = rc.overall.grade;
              k.position = rc.overall.position_ordinal;
              k.class_name = rc.class?.name;
              k.arm_name = rc.class?.arm_name;
              k.status = rc.status;
              k.term_label = rc.term_label;
            } catch (e) {
              // parent may hit 423 (draft) — that's fine, still show class name from student doc
              try {
                const s = (await api.get(`/students/${c.id}`)).data;
                k.class_name = s.current_class_name || null;
              } catch (e2) { /* silent */ }
              k.status = e?.response?.status === 423 ? "draft" : null;
            }
          }
          kpiOut[c.id] = k;
        }));
        setKpis(kpiOut);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-[#475569]"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your children…</div>;
  }

  if (children.length === 0) {
    return (
      <Card className="p-10 text-center border-[#E2E8F0]" data-testid="ph-empty">
        <ShieldAlert className="w-10 h-10 text-[#9DCD3F] mx-auto mb-3" />
        <h3 className="font-display text-xl text-[#1B2C5A]">No children linked to your account</h3>
        <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
          Your parent account is set up but no student is linked yet. Please contact the school office to link your child(ren) to your profile.
        </p>
      </Card>
    );
  }

  return (
    <div data-testid="parent-home">
      {/* Hero */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#9DCD3F] font-medium">Welcome</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1B2C5A] mt-1">Assalāmu ʿalaykum, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-[#475569] mt-1.5">You have <span className="text-[#1B2C5A] font-medium">{children.length}</span> child{children.length !== 1 ? "ren" : ""} enrolled at Darussalam. Tap any card to view their dashboard.</p>
        </div>
      </div>

      <FamilyPaymentCart sessions={sessions} />

      {/* Children grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ph-children-grid">
        {children.map((c) => {
          const k = kpis[c.id] || {};
          return (
            <button
              key={c.id}
              onClick={() => navigate(`/parent/child/${c.id}`)}
              className="group text-left rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#9DCD3F] hover:shadow-[0_8px_24px_-8px_rgba(157,205,63,0.35)] transition-all duration-300 overflow-hidden flex flex-col"
              data-testid={`ph-child-${c.id}`}
            >
              {/* Photo header */}
              <div className="relative h-32 bg-gradient-to-br from-[#1B2C5A] via-[#243671] to-[#1B2C5A] overflow-hidden">
                <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #9DCD3F 0%, transparent 50%)" }} />
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
                <div className="absolute left-4 -bottom-6 z-10">
                  {c.photo ? (
                    <img src={c.photo} alt="" className="w-16 h-16 rounded-2xl object-cover border-4 border-white shadow-lg group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-[#9DCD3F] border-4 border-white shadow-lg text-[#1B2C5A] grid place-items-center font-display text-xl group-hover:scale-105 transition-transform">
                      {(c.first_name?.[0] || c.full_name?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                </div>
                {k.status === "published" && (
                  <span className="absolute right-3 top-3 text-[10px] px-2 py-0.5 rounded-full bg-[#9DCD3F] text-[#1B2C5A] font-medium shadow-md">Report Ready</span>
                )}
                {k.status === "draft" && (
                  <span className="absolute right-3 top-3 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium shadow-sm border border-amber-200">In Progress</span>
                )}
              </div>

              <div className="pt-9 pb-4 px-4 flex-1 flex flex-col">
                <div className="min-h-[52px]">
                  <div className="font-display text-lg text-[#1B2C5A] leading-tight truncate">{c.full_name}</div>
                  <div className="text-xs text-[#475569] mt-0.5">
                    <span className="font-mono">{c.admission_no}</span>
                    {k.class_name && <> · {k.class_name}{k.arm_name ? ` · ${k.arm_name}` : ""}</>}
                  </div>
                </div>

                {/* KPI mini-grid */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <KpiMini icon={<Calendar className="w-3.5 h-3.5" />} label="Attend." value={k.attendance_pct != null ? `${k.attendance_pct}%` : "—"} tone={k.attendance_pct == null ? "neutral" : k.attendance_pct >= 75 ? "good" : "warn"} />
                  <KpiMini icon={<Award className="w-3.5 h-3.5" />} label="Avg" value={k.avg_pct != null ? `${k.avg_pct}%` : "—"} tone={k.avg_pct == null ? "neutral" : k.avg_pct >= 50 ? "good" : "warn"} />
                  <KpiMini icon={<GraduationCap className="w-3.5 h-3.5" />} label="Pos" value={k.position || "—"} tone="brand" />
                </div>

                <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between text-xs">
                  <span className="text-[#475569]">{k.term_label || "This Term"}</span>
                  <span className="inline-flex items-center gap-1 text-[#9DCD3F] font-medium group-hover:gap-2 transition-all">
                    Open <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const KpiMini = ({ icon, label, value, tone = "neutral" }) => {
  const toneCls = {
    good: "bg-emerald-50 text-emerald-700",
    warn: "bg-amber-50 text-amber-800",
    brand: "bg-[#9DCD3F]/15 text-[#1B2C5A]",
    neutral: "bg-slate-50 text-slate-600",
  }[tone];
  return (
    <div className={"rounded-lg p-2 " + toneCls}>
      <div className="flex items-center gap-1 opacity-70">{icon}<span className="text-[9px] uppercase tracking-wider">{label}</span></div>
      <div className="text-sm font-bold mt-0.5 truncate">{value}</div>
    </div>
  );
};

export default ParentHome;
